import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KEY_PATTERN, keyFor, slugify, typeDef, uniqueKey, type PropDef } from '../../featureTypes/registry';
import type { FeatureRecord, PropValue } from '../../lib/types';
import { protectedFileUrl, pb } from '../../lib/pb';
import { errorMessage } from '../../lib/errors';
import { currentMap, isAdmin, isEditor, toast, useStore } from '../../store/store';
import { takenKeys, updateFeature } from '../../store/actions';
import { Icon } from '../Icon';

interface Draft {
  name: string;
  key: string;
  description: string;
  tags: string;
  showInGame: boolean;
  color: string;
  parent: string;
  props: Record<string, PropValue>;
}

function draftOf(f: FeatureRecord): Draft {
  return {
    name: f.name,
    key: f.key,
    description: f.description ?? '',
    tags: (f.tags ?? []).join(', '),
    showInGame: f.showInGame,
    color: f.color ?? '',
    parent: f.parent ?? '',
    props: { ...(f.properties ?? {}), ...(f.designProps ?? {}) },
  };
}

export function PropertiesTab({ feature: f }: { feature: FeatureRecord }) {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const features = useStore((s) => s.features);
  const def = typeDef(f.type);
  const editor = isEditor(user);
  const admin = isAdmin(user);
  // `base` is the revision my edits started from. Saving sends base.rev, so if someone else saved in
  // between, the server answers 409 and the conflict banner appears instead of silently overwriting them.
  const [base, setBase] = useState(() => ({ rev: f.rev, draft: draftOf(f) }));
  const [draft, setDraft] = useState<Draft>(base.draft);
  const [saving, setSaving] = useState(false);

  const dirty = JSON.stringify(draft) !== JSON.stringify(base.draft);
  // Someone else saved while I had no local changes: follow along (state update during render).
  if (f.rev !== base.rev && !dirty) {
    const next = { rev: f.rev, draft: draftOf(f) };
    setBase(next);
    setDraft(next.draft);
  }
  const resetToLatest = () => {
    const next = { rev: f.rev, draft: draftOf(f) };
    setBase(next);
    setDraft(next.draft);
  };

  const keyLocked = !admin && (!!f.unityKey || f.status === 'in_unity' || f.status === 'needs_update');
  const keyValid = KEY_PATTERN.test(draft.key);
  const readOnly = !editor;

  const parentOptions = useMemo(
    () => (def.parentTypes ? Object.values(features).filter((x) => def.parentTypes!.includes(x.type)) : []),
    [features, def.parentTypes],
  );

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setProp = (name: string, v: PropValue) =>
    setDraft((d) => ({ ...d, props: { ...d.props, [name]: v } }));

  const save = async () => {
    setSaving(true);
    const properties: Record<string, PropValue> = {};
    const designProps: Record<string, PropValue> = {};
    for (const p of def.props) {
      const v = draft.props[p.name];
      if (v === undefined || v === '') continue;
      (p.design ? designProps : properties)[p.name] = v;
    }
    const rec = await updateFeature(
      f.id,
      {
        name: draft.name,
        key: draft.key,
        description: draft.description,
        tags: draft.tags
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        showInGame: draft.showInGame,
        color: draft.color,
        parent: draft.parent,
        properties,
        designProps,
      },
      base.rev,
    );
    setSaving(false);
    if (rec) {
      const next = { rev: rec.rev, draft: draftOf(rec) };
      setBase(next);
      setDraft(next.draft);
    }
  };

  const derivedKey = (name: string) => {
    const map = currentMap();
    if (!map) return null;
    const taken = takenKeys();
    taken.delete(f.key);
    return uniqueKey(keyFor(f.type, map.keyPrefix, slugify(name)), taken);
  };
  const keyFromName = () => {
    const k = derivedKey(draft.name);
    if (k) set({ key: k });
  };
  /** While the key is still the one generated from the name, renaming updates it too. */
  const rename = (name: string) => {
    const map = currentMap();
    const auto = map ? keyFor(f.type, map.keyPrefix, slugify(draft.name)) : null;
    // Auto keys are "<derived>" or "<derived>_<n>" (uniqueKey's suffix).
    const isAuto =
      !!auto &&
      (draft.key === auto ||
        (draft.key.startsWith(`${auto}_`) && /^\d+$/.test(draft.key.slice(auto.length + 1))));
    if (isAuto && !keyLocked && !readOnly) set({ name, key: derivedKey(name) ?? draft.key });
    else set({ name });
  };

  return (
    <div className="props">
      <label>
        {t('common.name')}
        <input value={draft.name} onChange={(e) => rename(e.target.value)} readOnly={readOnly} />
      </label>
      <label>
        {t('common.key')} {keyLocked && <span className="muted small">({t('inspector.keyLocked')})</span>}
        <div className="row">
          <input
            className={`mono${keyValid ? '' : ' invalid'}`}
            value={draft.key}
            onChange={(e) => set({ key: e.target.value.toLowerCase() })}
            readOnly={readOnly || keyLocked}
          />
          {!readOnly && !keyLocked && (
            <button className="small" title={t('inspector.keyFromName')} onClick={keyFromName}>
              ↻
            </button>
          )}
        </div>
      </label>
      <label>
        {t('common.description')}
        <textarea
          rows={3}
          value={draft.description}
          onChange={(e) => set({ description: e.target.value })}
          readOnly={readOnly}
        />
      </label>

      {def.parentTypes && (
        <label>
          {t('inspector.parent')}
          <select value={draft.parent} onChange={(e) => set({ parent: e.target.value })} disabled={readOnly}>
            <option value="">—</option>
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="prop-grid">
        {def.props.map((p) => (
          <PropField
            key={p.name}
            prop={p}
            value={draft.props[p.name]}
            onChange={(v) => setProp(p.name, v)}
            readOnly={readOnly || (!!p.unityId && keyLocked)}
          />
        ))}
      </div>

      <div className="row wrap">
        <label className="check-row">
          <input
            type="checkbox"
            checked={draft.showInGame}
            onChange={() => set({ showInGame: !draft.showInGame })}
            disabled={readOnly}
          />
          {t('inspector.showInGame')}
        </label>
        <label className="inline">
          {t('inspector.color')}
          <input
            type="color"
            value={draft.color || def.color}
            onChange={(e) => set({ color: e.target.value })}
            disabled={readOnly}
          />
          {draft.color && !readOnly && (
            <button className="link small" onClick={() => set({ color: '' })}>
              {t('common.reset')}
            </button>
          )}
        </label>
      </div>
      <label>
        {t('common.tags')}
        <input
          value={draft.tags}
          onChange={(e) => set({ tags: e.target.value })}
          placeholder={t('inspector.tagsHint')}
          readOnly={readOnly}
        />
      </label>

      {editor && (
        <div className="save-row">
          {dirty && <span className="muted small">{t('inspector.unsaved')}</span>}
          <button onClick={resetToLatest} disabled={!dirty || saving}>
            {t('common.cancel')}
          </button>
          <button
            className="primary"
            onClick={() => void save()}
            disabled={!dirty || saving || !keyValid || !draft.name.trim()}
          >
            {t('common.save')}
          </button>
        </div>
      )}

      <Attachments feature={f} canEdit={editor} />
    </div>
  );
}

function PropField({
  prop: p,
  value,
  onChange,
  readOnly,
}: {
  prop: PropDef;
  value: PropValue;
  onChange: (v: PropValue) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const features = useStore((s) => s.features);
  const catalog = useStore((s) => s.maps.find((m) => m.id === s.mapId)?.unityCatalog);
  const label = (
    <span className="prop-label">
      {t(`props.${p.name}`, { defaultValue: p.name })}
      {p.design && <span className="tag-design">{t('inspector.designOnly')}</span>}
      {p.unityId && <span className="tag-unity">Unity id</span>}
    </span>
  );

  switch (p.kind) {
    case 'bool':
      return (
        <label className="check-row">
          <input type="checkbox" checked={!!value} onChange={() => onChange(!value)} disabled={readOnly} />
          {label}
        </label>
      );
    case 'enum':
      return (
        <label>
          {label}
          <select
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            disabled={readOnly}
          >
            <option value="">—</option>
            {p.options!.map((o) => (
              <option key={o} value={o}>
                {t(`enums.${p.name}.${o}`, { defaultValue: o })}
              </option>
            ))}
          </select>
        </label>
      );
    case 'enumMulti': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset className="multi">
          <legend>{label}</legend>
          {p.options!.map((o) => (
            <label key={o} className="check-row">
              <input
                type="checkbox"
                checked={arr.includes(o)}
                disabled={readOnly}
                onChange={() => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o])}
              />
              {t(`enums.${p.name}.${o}`, { defaultValue: o })}
            </label>
          ))}
        </fieldset>
      );
    }
    case 'int':
    case 'number':
      return (
        <label>
          {label}
          <input
            type="number"
            step={p.kind === 'int' ? 1 : 'any'}
            value={value === undefined || value === null ? '' : String(value)}
            onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
            readOnly={readOnly}
          />
        </label>
      );
    case 'color':
      return (
        <label>
          {label}
          <input
            type="color"
            value={(value as string) || '#ffffff'}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
          />
        </label>
      );
    case 'textarea':
      return (
        <label className="span2">
          {label}
          <textarea
            rows={2}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
          />
        </label>
      );
    case 'featureRef': {
      const opts = Object.values(features).filter((x) => p.refTypes?.includes(x.type));
      return (
        <label>
          {label}
          <select
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || undefined)}
            disabled={readOnly}
          >
            <option value="">—</option>
            {opts.map((o) => (
              <option key={o.id} value={o.key}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
      );
    }
    case 'featureRefs': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      const opts = Object.values(features).filter((x) => p.refTypes?.includes(x.type));
      return (
        <fieldset className="multi span2">
          <legend>{label}</legend>
          {opts.length === 0 && <span className="muted small">—</span>}
          {opts.map((o) => (
            <label key={o.id} className="check-row">
              <input
                type="checkbox"
                checked={arr.includes(o.key)}
                disabled={readOnly}
                onChange={() =>
                  onChange(arr.includes(o.key) ? arr.filter((x) => x !== o.key) : [...arr, o.key])
                }
              />
              {o.name}
            </label>
          ))}
        </fieldset>
      );
    }
    case 'catalog': {
      const list = (catalog?.[p.catalog!] ?? []) as { name?: string; id?: string }[];
      const listId = `cat-${p.catalog}`;
      return (
        <label>
          {label}
          <input
            list={listId}
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
          />
          <datalist id={listId}>
            {list.map((c) => {
              const v = c.id ?? c.name ?? '';
              return <option key={v} value={v} />;
            })}
          </datalist>
        </label>
      );
    }
    case 'list': {
      const rows = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
      const setRow = (i: number, name: string, v: unknown) =>
        onChange(rows.map((r, j) => (j === i ? { ...r, [name]: v } : r)) as Record<string, unknown>[]);
      return (
        <fieldset className="multi span2">
          <legend>{label}</legend>
          {rows.map((r, i) => (
            <div key={i} className="list-row">
              {p.items!.map((sub) => (
                <PropField
                  key={sub.name}
                  prop={sub}
                  value={r[sub.name] as PropValue}
                  onChange={(v) => setRow(i, sub.name, v)}
                  readOnly={readOnly}
                />
              ))}
              {!readOnly && (
                <button className="ghost icon-only" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                  <Icon id="trash" />
                </button>
              )}
            </div>
          ))}
          {!readOnly && (
            <button
              className="small"
              onClick={() =>
                onChange([
                  ...rows,
                  Object.fromEntries(
                    p.items!.filter((s) => s.default !== undefined).map((s) => [s.name, s.default]),
                  ),
                ])
              }
            >
              + {t('common.add')}
            </button>
          )}
        </fieldset>
      );
    }
    default:
      return (
        <label>
          {label}
          <input
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            readOnly={readOnly}
            className={p.unityId ? 'mono' : ''}
          />
        </label>
      );
  }
}

function Attachments({ feature: f, canEdit }: { feature: FeatureRecord; canEdit: boolean }) {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<{ name: string; thumb: string; full: string }[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = [];
      for (const name of f.files ?? []) {
        const isImg = /\.(png|jpe?g|webp|gif)$/i.test(name);
        out.push({
          name,
          thumb: isImg ? await protectedFileUrl(f, name, '200x200') : '',
          full: await protectedFileUrl(f, name),
        });
      }
      if (!cancelled) setUrls(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [f]);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      for (const file of Array.from(files)) fd.append('files+', file);
      fd.append('rev', String(f.rev));
      await pb.collection('features').update(f.id, fd);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };
  const remove = async (name: string) => {
    try {
      await pb.collection('features').update(f.id, { 'files-': [name], rev: f.rev });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  if (!canEdit && urls.length === 0) return null;
  return (
    <div className="attachments">
      <div className="group-label">{t('inspector.attachments')}</div>
      <div className="thumbs">
        {urls.map((u) => (
          <div key={u.name} className="thumb">
            <a href={u.full} target="_blank" rel="noreferrer">
              {u.thumb ? <img src={u.thumb} alt={u.name} /> : <span className="small">{u.name}</span>}
            </a>
            {canEdit && (
              <button
                className="ghost icon-only"
                onClick={() => void remove(u.name)}
                title={t('common.delete')}
              >
                <Icon id="close" size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
      {canEdit && (
        <label className="upload">
          <Icon id="tool-upload" /> {busy ? t('common.loading') : t('inspector.addFiles')}
          <input type="file" multiple hidden onChange={(e) => void upload(e.target.files)} />
        </label>
      )}
    </div>
  );
}
